import {
  Box,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import MicIcon from "@mui/icons-material/Mic";
import MicNoneIcon from "@mui/icons-material/MicNone";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
// import PublicIcon from "@mui/icons-material/Public";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import { useApp } from "../../../app/context/useApp";
import useVoiceChat, {
  isSpeakableAssistantText,
} from "../hooks/useVoiceChat";
import {
  getAllModelsByProviderId,
  getAllProviders,
} from "../../../core/services/aiProviderModel.service";
import sendDefault from "../../../assets/send_default.png";
import sendHover from "../../../assets/send_hover.png";
import {
  HOME_GLASS_BACKDROP,
  HOME_GLASS_BORDER,
  HOME_GLASS_SHADOW_HERO,
  homeGlassHero,
} from "../constants/homeGlass";
import { mobileNavMediaQuery } from "../../../layout/layoutMetrics";
import ProviderTierSelect from "./ProviderTierSelect";
import {
  DEFAULT_MODEL_TIER,
  resolveModelForTier,
} from "./providerModelTiers";
import { canUseChatAttachments } from "../utils/siloPathUtils";

const _preloadHover = new Image();
_preloadHover.src = sendHover;

// Survives Home remounting QueryInput (hero empty state → chat composer)
// so the first voice reply can still be spoken.
const pendingVoiceSpeakRef = { current: false };

export default function QueryInput({ layout = "inline", pinToolbar = false }) {
  const [input, setInput] = useState("");
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachMenuAnchor, setAttachMenuAnchor] = useState(null);
  const [isSendHovered, setIsSendHovered] = useState(false);
  const [providers, setProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [models, setModels] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState("");
  const [loadingModels, setLoadingModels] = useState(false);
  const modelsCacheRef = useRef(new Map());
  const modelsRequestRef = useRef(new Map());
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const singleLineHeightRef = useRef(0);
  const [isMultiline, setIsMultiline] = useState(false);
  const isMobileNav = useMediaQuery(mobileNavMediaQuery);
  const location = useLocation();
  const {
    selectedClient,
    selectedSiloIsDefault,
    sendQuery,
    stopGenerating,
    isGenerating,
    messages,
    showAlert,
    useWebSearch,
    // setUseWebSearch,
    selectedProviderId,
    setSelectedProviderId,
    selectedModelTier,
    setSelectedModelTier,
  } = useApp();
  const selectedTier = selectedModelTier || DEFAULT_MODEL_TIER;
  const setSelectedTier = setSelectedModelTier;
  const requiresClientSelection = !selectedSiloIsDefault;
  const inputDisabled = (requiresClientSelection && !selectedClient) || isGenerating;
  const showProviderSelect = selectedSiloIsDefault;
  const attachmentsEnabled = canUseChatAttachments(
    location.pathname,
    selectedSiloIsDefault,
  );
  const canAttach = attachmentsEnabled && !inputDisabled;
  const attachMenuOpen = Boolean(attachMenuAnchor);

  useEffect(() => {
    if (!canAttach) setAttachMenuAnchor(null);
  }, [canAttach]);

  const applyModels = useCallback((list) => {
    setModels(list);
  }, []);

  const loadModelsForProvider = useCallback(
    async (
      providerId,
      { showLoading = true, applyToState = true } = {},
    ) => {
      const key = String(providerId ?? "");
      if (!key) {
        if (applyToState) {
          setModels([]);
          setSelectedModelId("");
        }
        return [];
      }

      const cached = modelsCacheRef.current.get(key);
      if (cached) {
        if (applyToState) applyModels(cached);
        return cached;
      }

      if (modelsRequestRef.current.has(key)) {
        const list = await modelsRequestRef.current.get(key);
        if (applyToState) applyModels(list);
        return list;
      }

      if (showLoading && applyToState) setLoadingModels(true);

      const request = getAllModelsByProviderId(key)
        .then((list) => {
          modelsCacheRef.current.set(key, list);
          return list;
        })
        .catch(() => [])
        .finally(() => {
          modelsRequestRef.current.delete(key);
        });

      modelsRequestRef.current.set(key, request);

      try {
        const list = await request;
        if (applyToState) applyModels(list);
        return list;
      } finally {
        if (showLoading && applyToState) setLoadingModels(false);
      }
    },
    [applyModels],
  );

  useEffect(() => {
    if (!showProviderSelect) return;

    let cancelled = false;

    const loadProviders = async () => {
      setLoadingProviders(true);
      try {
        const list = await getAllProviders();
        if (cancelled) return;
        setProviders(list);
        setSelectedProviderId((prev) => {
          if (prev && list.some((p) => String(p.providerId) === String(prev))) {
            return prev;
          }
          return list[0]?.providerId != null ? String(list[0].providerId) : "";
        });
      } catch {
        if (!cancelled) {
          setProviders([]);
          setSelectedProviderId("");
        }
      } finally {
        if (!cancelled) setLoadingProviders(false);
      }
    };

    loadProviders();
    return () => {
      cancelled = true;
    };
  }, [showProviderSelect]);

  useEffect(() => {
    if (!showProviderSelect || !selectedProviderId) {
      setModels([]);
      setSelectedModelId("");
      return;
    }

    loadModelsForProvider(selectedProviderId, {
      showLoading: true,
    });
  }, [showProviderSelect, selectedProviderId, loadModelsForProvider]);

  useEffect(() => {
    if (!showProviderSelect) return;

    if (!models.length) {
      setSelectedModelId("");
      return;
    }

    const providerName = providers.find(
      (provider) => String(provider.providerId) === String(selectedProviderId),
    )?.providerName;

    const current = resolveModelForTier(models, providerName, selectedTier);
    if (current?.modelId != null) {
      setSelectedModelId(String(current.modelId));
      return;
    }

    for (const tierId of ["medium", "max", "low"]) {
      const match = resolveModelForTier(models, providerName, tierId);
      if (match?.modelId != null) {
        if (tierId !== selectedTier) setSelectedTier(tierId);
        setSelectedModelId(String(match.modelId));
        return;
      }
    }

    setSelectedModelId(String(models[0].modelId));
  }, [
    showProviderSelect,
    models,
    selectedTier,
    selectedProviderId,
    providers,
  ]);

  const handleProviderHover = useCallback(
    (providerId) => {
      const key = String(providerId ?? "");
      if (!key) return;
      if (modelsCacheRef.current.has(key) || modelsRequestRef.current.has(key)) {
        return;
      }
      loadModelsForProvider(key, {
        showLoading: false,
        applyToState: false,
      });
    },
    [loadModelsForProvider],
  );

  const sendingRef = useRef(false);
  const handleSendRef = useRef(async () => {});

  const {
    status: voiceStatus,
    errorMessage: voiceErrorMessage,
    isRecognitionSupported,
    isTtsSupported,
    isRecordingFallbackSupported,
    isListening,
    isSpeaking,
    startListening,
    stopListening,
    stopSpeaking,
    releaseVoiceBusy,
    speak,
  } = useVoiceChat({
    enabled: !(requiresClientSelection && !selectedClient),
    onTranscript: (liveText) => setInput(liveText),
    onFinalTranscript: (finalText) => {
      setInput(finalText);
      if (!finalText) {
        pendingVoiceSpeakRef.current = false;
        return;
      }
      if (sendingRef.current || isGenerating) {
        pendingVoiceSpeakRef.current = false;
        return;
      }
      pendingVoiceSpeakRef.current = true;
      handleSendRef.current(finalText);
    },
    onError: ({ type, message }) => {
      pendingVoiceSpeakRef.current = false;
      if (
        type === "no-speech" ||
        type === "aborted" ||
        type === "stopped" ||
        type === "tts-unavailable"
      ) {
        return;
      }
      showAlert("Voice input", message);
    },
  });
  const voiceInputAvailable =
    isRecognitionSupported || isRecordingFallbackSupported;

  const handleSend = async (rawText) => {
    if (isGenerating || sendingRef.current) return;
    const trimmedInput = (
      typeof rawText === "string" ? rawText : input
    ).trim();
    const fileToSend = attachmentsEnabled ? attachmentFile : null;
    if (!trimmedInput && !fileToSend) {
      pendingVoiceSpeakRef.current = false;
      return;
    }

    stopListening();
    stopSpeaking();
    setInput("");
    setAttachmentFile(null);
    sendingRef.current = true;
    const modelOpts =
      showProviderSelect && selectedModelId
        ? { modelId: Number(selectedModelId) }
        : {};
    try {
    await sendQuery(selectedClient, trimmedInput, {
      useWebSearch,
      ...modelOpts,
      ...(fileToSend ? { attachment: fileToSend } : {}),
    });
    } finally {
      sendingRef.current = false;
    }
  };
  handleSendRef.current = handleSend;

  const handleStopGenerating = () => {
    pendingVoiceSpeakRef.current = false;
    releaseVoiceBusy();
    stopGenerating();
  };

  const handleMicClick = () => {
    if (!voiceInputAvailable) {
      showAlert(
        "Voice input unavailable",
        "Voice input is not available in this browser. Use Chrome, Edge, or Firefox on HTTPS or localhost, and allow microphone access.",
      );
      return;
    }
    if (isListening) {
      stopListening();
      return;
    }
    if (isSpeaking) {
      releaseVoiceBusy();
      return;
    }
    if (inputDisabled) return;
    startListening();
  };

  useEffect(() => {
    if (isGenerating) stopListening();
  }, [isGenerating, stopListening]);

  useEffect(() => {
    if (isGenerating || !pendingVoiceSpeakRef.current) return;

    const lastAiMessage = [...messages]
      .reverse()
      .find((message) => message?.role === "ai");
    const spokenText =
      lastAiMessage?.displayContent ?? lastAiMessage?.content ?? "";

    // First Home turn remounts this composer while the stream is still
    // empty; wait until the assistant text is actually present.
    if (!lastAiMessage || !String(spokenText).trim()) return;

    pendingVoiceSpeakRef.current = false;
   if (isTtsSupported && isSpeakableAssistantText(spokenText)) {
        speak(spokenText);
    } else {
      releaseVoiceBusy();
      }
  }, [isGenerating, isTtsSupported, messages, releaseVoiceBusy, speak]);

  useEffect(() => {
    if (!attachmentsEnabled) {
      setAttachmentFile(null);
    }
  }, [attachmentsEnabled]);

  const canSend =
    Boolean(input.trim() || (attachmentsEnabled && attachmentFile)) &&
    !inputDisabled &&
    !isListening;

  const voiceBusy = isGenerating || voiceStatus === "processing";
  const micDisabled = voiceInputAvailable
    ? isSpeaking
      ? false
      : voiceBusy ||
        (!isListening && requiresClientSelection && !selectedClient)
    : false;
  const voiceTooltip = !voiceInputAvailable
    ? "Voice input is not available in this browser"
    : isListening
      ? "Stop listening"
      : isSpeaking
        ? "Stop speaking"
        : voiceBusy
          ? "Processing..."
          : voiceStatus === "error" && voiceErrorMessage
            ? voiceErrorMessage
            : requiresClientSelection && !selectedClient
              ? "Select a client first"
              : "Voice input";

  const isHero = layout === "hero";
  const stacked = isHero || pinToolbar || isMultiline;
  const sendIconSize = isHero ? 48 : 46;
  const stopIconSize = isHero ? 36 : 34;

  const updateMultiline = useCallback(() => {
    const el = textareaRef.current;
    if (!el) {
      setIsMultiline(false);
      return;
    }
    if (!el.value) {
      singleLineHeightRef.current = el.scrollHeight;
      setIsMultiline(false);
      return;
    }
    if (el.value.includes("\n")) {
      setIsMultiline(true);
      return;
    }
    const baseline = singleLineHeightRef.current || el.scrollHeight;
    const grewPastOneLine = el.scrollHeight > baseline + 4;
    if (grewPastOneLine) {
      setIsMultiline(true);
      return;
    }
    if (el.value.length < 40) {
      setIsMultiline(false);
    }
  }, []);

  useEffect(() => {
    updateMultiline();
  }, [input, updateMultiline]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => updateMultiline());
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateMultiline]);

  const actionIconSx = isHero
    ? { fontSize: 22, color: "rgba(255,255,255,0.72)", display: "block" }
    : undefined;

  // const webSearchIconSx = isHero
  //   ? {
  //       fontSize: 22,
  //       color: useWebSearch ? "rgba(255, 193, 7, 0.9)" : "rgba(255,255,255,0.72)",
  //     }
  //   : undefined;

  const providerTierSelect = (
    <ProviderTierSelect
      isHero={isHero}
      providers={providers}
      selectedProviderId={selectedProviderId}
      onProviderChange={setSelectedProviderId}
      onProviderHover={handleProviderHover}
      selectedTier={selectedTier}
      onTierChange={setSelectedTier}
      loadingProviders={loadingProviders}
      loadingModels={loadingModels}
    />
  );

  const iconRow = (
    <>
      {/* <Tooltip title={useWebSearch ? "Web Search: ON" : "Web Search: OFF"}>
        <IconButton
          onClick={() => setUseWebSearch(!useWebSearch)}
          size={isHero ? "small" : "medium"}
          sx={{
            ...(isHero
              ? { p: "6px", color: useWebSearch ? "rgba(255, 193, 7, 0.9)" : "rgba(255,255,255,0.72)" }
              : {
                  color: useWebSearch
                    ? "rgba(255, 193, 7, 0.9)"
                    : "rgba(255,255,255,0.4)",
                }),
            transition: "all 0.3s ease",
            "&:hover": {
              color: useWebSearch ? "rgba(255, 193, 7, 1)" : "rgba(255,255,255,0.9)",
              transform: "scale(1.08)",
            },
          }}
        >
          <PublicIcon sx={webSearchIconSx} />
        </IconButton>
      </Tooltip> */}

      {showProviderSelect ? providerTierSelect : null}

      <Tooltip title={voiceTooltip} placement="top">
        <span>
        <IconButton
          aria-label={voiceTooltip}
            aria-pressed={isListening || isSpeaking}
          size={isHero ? "small" : "medium"}
            onClick={handleMicClick}
            disabled={micDisabled}
          sx={{
            p: isHero ? "6px" : "8px",
            color: isListening
                ? "rgba(255, 193, 7, 0.95)"
                : isSpeaking
                  ? "rgba(255,255,255,0.95)"
                  : "rgba(255,255,255,0.72)",
            cursor: micDisabled ? "not-allowed" : "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            "& .MuiSvgIcon-root": { display: "block" },
              ...(isListening
                ? {
                    animation: "voiceMicPulse 1.4s ease-in-out infinite",
                    "@keyframes voiceMicPulse": {
                      "0%, 100%": { opacity: 1, transform: "scale(1)" },
                      "50%": { opacity: 0.65, transform: "scale(1.08)" },
                    },
                  }
                : {}),
              "&:hover": micDisabled
                ? {}
                : {
                    color: isListening
                      ? "rgba(255, 193, 7, 1)"
                      : "rgba(255,255,255,0.95)",
                  },
            }}
          >
            {isSpeaking ? (
              <VolumeUpRoundedIcon sx={actionIconSx} />
            ) : isListening ? (
              <MicIcon sx={actionIconSx} />
            ) : (
          <MicNoneIcon sx={actionIconSx} />
            )}
        </IconButton>
        </span>
      </Tooltip>

      <Tooltip
        title={isGenerating ? "Stop streaming" : "Send message"}
        placement="top"
      >
        <Box
          component="button"
          onClick={isGenerating ? handleStopGenerating : handleSend}
          disabled={isGenerating ? false : !canSend}
          aria-label={isGenerating ? "Stop streaming" : "Send message"}
          onMouseEnter={() => setIsSendHovered(true)}
          onMouseLeave={() => setIsSendHovered(false)}
          sx={{
            background: "none",
            border: "none",
            padding: "0",
            cursor:
              isGenerating || canSend ? "pointer" : "not-allowed",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: isGenerating || canSend ? 1 : 0.35,
            transition: "opacity 0.2s ease",
            flexShrink: 0,
            width: sendIconSize,
            height: sendIconSize,
            lineHeight: 0,
            alignSelf: "center",
          }}
        >
          {isGenerating ? (
            <StopRoundedIcon
              sx={{
                fontSize: stopIconSize,
                color: "rgba(255,255,255,0.95)",
                display: "block",
              }}
            />
          ) : (
            <img
              src={isSendHovered ? sendHover : sendDefault}
              alt="Send"
              style={{
                width: sendIconSize,
                height: sendIconSize,
                objectFit: "contain",
                display: "block",
              }}
            />
          )}
        </Box>
      </Tooltip>
    </>
  );

  const closeAttachMenu = () => setAttachMenuAnchor(null);

  const plusButton = (
    <Tooltip
      title={
        attachMenuOpen
          ? ""
          : attachmentsEnabled
            ? attachmentFile
              ? attachmentFile.name
              : "Add file"
            : "Attachments are only available on Home and Brief"
      }
      placement="top"
    >
      <span>
        <IconButton
          aria-label="Add file"
          aria-haspopup="menu"
          aria-expanded={attachMenuOpen}
          size={isHero ? "small" : "medium"}
          onClick={(e) => {
            if (!canAttach) return;
            setAttachMenuAnchor(e.currentTarget);
          }}
          sx={{
            p: isHero ? "6px" : "8px",
            color: canAttach
              ? "rgba(255,255,255,0.88)"
              : "rgba(255,255,255,0.4)",
            cursor: canAttach ? "pointer" : "not-allowed",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            background: attachMenuOpen ? "rgba(255,255,255,0.14)" : "transparent",
            "& .MuiSvgIcon-root": { display: "block" },
            "&:hover": canAttach
              ? {
                  color: "rgba(255,255,255,0.95)",
                  background: "rgba(255,255,255,0.12)",
                }
              : {},
          }}
        >
          <AddIcon
            sx={{
              fontSize: isHero ? 24 : 22,
              display: "block",
            }}
          />
        </IconButton>
      </span>
    </Tooltip>
  );

  const attachMenu = (
    <Menu
      anchorEl={attachMenuAnchor}
      open={attachMenuOpen}
      onClose={closeAttachMenu}
      disableScrollLock
      anchorOrigin={{
        vertical: isHero ? "bottom" : "top",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: isHero ? "top" : "bottom",
        horizontal: "left",
      }}
      slotProps={{
        paper: {
          sx: {
            mt: isHero ? 1.5 : 0,
            mb: isHero ? 0 : 1,
            minWidth: { xs: 260, sm: 340 },
            borderRadius: "15px",
            bgcolor: "transparent",
            backgroundImage: "none",
            background: homeGlassHero.rest,
            border: HOME_GLASS_BORDER,
            backdropFilter: HOME_GLASS_BACKDROP,
            WebkitBackdropFilter: HOME_GLASS_BACKDROP,
            boxShadow: HOME_GLASS_SHADOW_HERO,
            overflow: "hidden",
          },
        },
        list: {
          sx: { py: 0.5, px: 0.25, background: "transparent" },
        },
      }}
    >
      <MenuItem
        onClick={() => {
          closeAttachMenu();
          fileInputRef.current?.click();
        }}
        sx={{
          // py: 1.15,
          px: 0.5,
          mx: 0.5,
          gap: 1.25,
          borderRadius: "14px",
          background: "transparent",
          "&:hover": {
            background: homeGlassHero.hover,
          },
        }}
      >
        <AttachFileIcon
          sx={{ fontSize: 20, color: "rgba(255,255,255,0.92)", flexShrink: 0 }}
        />
        <Typography
          sx={{
            flex: 1,
            fontSize: 14,
            fontWeight: 500,
            color: "rgba(255,255,255,0.95)",
            fontFamily: "'Poppins', sans-serif",
          }}
        >
          Add file
        </Typography>
      </MenuItem>
    </Menu>
  );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: stacked ? "stretch" : "center",
        gap: 1.5,
        mx: "auto",
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: stacked ? "column" : "row",
          alignItems: stacked ? "stretch" : "center",
          position: "relative",
          overflow: "hidden",
          minHeight: isHero
            ? { xs: 50, sm: "clamp(50px, 10vw, 112px)" }
            : stacked
              ? 72
              : undefined,
          borderRadius: stacked ? (isHero ? "20px" : "24px") : "34px",
          px: isHero ? { xs: 2, sm: 2.5 } : 2,
          py: stacked ? 0.75 : 0.5,
          pt: stacked ? (isHero ? { xs: 0.85, sm: 1 } : 1) : undefined,
          pb: stacked ? (isHero ? { xs: 0.25, sm: 0.35 } : 0.35) : undefined,
          ...(isHero
            ? {
                border: HOME_GLASS_BORDER,
                backdropFilter: HOME_GLASS_BACKDROP,
                WebkitBackdropFilter: HOME_GLASS_BACKDROP,
                background: homeGlassHero.rest,
                boxShadow: HOME_GLASS_SHADOW_HERO,
                transition: "background 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  background: homeGlassHero.hover,
                },
              }
            : {
                border: "1px solid rgba(255,255,255,0.15)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                background: "rgba(255,255,255,0.15)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                transition: "background 0.3s ease",
                "&:hover": {
                  background: "rgba(255,255,255,0.25)",
                },
              }),
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept=".pdf,.txt,.doc,.docx,.md,.csv"
          disabled={!canAttach}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file && attachmentsEnabled) {
              setAttachmentFile(file);
            }
            e.target.value = "";
          }}
        />
        {attachMenu}
        <Box
          sx={{
            width: "100%",
            minWidth: 0,
            flex: stacked ? "1 1 auto" : "1 1 0",
            display: "flex",
            flexDirection: "column",
          }}
        >
        {attachmentsEnabled && attachmentFile ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              pt: 0.75,
              px: 0.5,
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                maxWidth: "100%",
                px: 1,
                py: 0.25,
                borderRadius: "999px",
                background: homeGlassHero.rest,
                border: HOME_GLASS_BORDER,
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.24)",
              }}
            >
              <AttachFileIcon sx={{ fontSize: 14, color: "rgba(255,255,255,0.8)" }} />
              <Box
                component="span"
                sx={{
                  fontSize: 12,
                  color: "rgba(255,255,255,0.88)",
                  maxWidth: 180,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontFamily: "'Poppins', sans-serif",
                }}
              >
                {attachmentFile.name}
              </Box>
              <IconButton
                aria-label="Remove attachment"
                size="small"
                onClick={() => setAttachmentFile(null)}
                sx={{
                  p: "2px",
                  color: "rgba(255,255,255,0.7)",
                  "&:hover": { color: "rgba(255,255,255,0.95)" },
                }}
              >
                <CloseRoundedIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          </Box>
        ) : null}
        <TextField
          fullWidth
          multiline
          minRows={1}
          maxRows={8}
          variant="standard"
          value={input}
          disabled={inputDisabled}
          inputRef={textareaRef}
          placeholder={
            requiresClientSelection && !selectedClient
              ? "Select a client first"
              : "Ask anything about your campaigns..."
          }
          autoComplete="off"
          inputProps={{
            autoComplete: "off",
            "data-1p-ignore": true,
            "data-lpignore": "true",
          }}
          onChange={(e) => {
            setInput(e.target.value);
            requestAnimationFrame(updateMultiline);
          }}
          onFocus={(e) => {
            if (!isMobileNav) return;
            requestAnimationFrame(() => {
              e.target?.scrollIntoView?.({
                block: "nearest",
                behavior: "smooth",
              });
            });
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          InputProps={{
            disableUnderline: true,
          }}
          sx={{
            width: "100%",
            minWidth: 0,
            "& .MuiInputBase-root": {
              alignItems: stacked ? "flex-start" : "center",
              paddingTop: 0,
              paddingBottom: 0,
            },
            "& textarea": {
              boxSizing: "border-box",
              padding: "10px 8px 10px 4px",
              overflowY: "auto !important",
              color: isHero ? "rgba(255,255,255,0.96)" : "rgba(255,255,255,0.85)",
              fontSize: isHero ? { xs: 16, sm: 17 } : 15,
              fontFamily: "'Poppins', sans-serif !important",
              letterSpacing: "0.2px",
              verticalAlign: "top",
              wordBreak: "break-word",
            },
            "& textarea::placeholder": isHero
              ? { color: "rgba(209, 213, 219, 0.85)", opacity: 1 }
              : {},
          }}
        />
        </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: stacked ? "flex-end" : "center",
            justifyContent: stacked ? "space-between" : "flex-end",
            gap: stacked ? 1 : 0.25,
              flexShrink: 0,
              minHeight: sendIconSize,
            ...(stacked
              ? {
              pt: 0.25,
              pb: 0,
              mx: { xs: -1.25, sm: -1.75 },
              mb: { xs: -0.15, sm: -0.2 },
              px: 0.5,
                }
              : {
                  ml: 0.5,
                }),
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: sendIconSize,
                flexShrink: 0,
                ml: stacked ? { xs: 0.5, sm: 0.75 } : 0,
              }}
            >
              {plusButton}
            </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: sendIconSize,
              gap: 0.75,
              ml: stacked ? "auto" : 0,
              flexShrink: 0,
            }}
          >
            {iconRow}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
